'use client';

import type { FeedbackQuestion } from '@up2stay/up2promo-api-client';
import { GuestFeedbackQuestionFields } from '@up2stay/up2promo-ui/components/guest-feedback-question-fields';
import {
  Label,
  PublicProfileBranding,
  PublicWorkspaceIdentityRow,
  Textarea,
  cn,
} from '@up2stay/up2promo-ui';
import { Star } from 'lucide-react';
import type { CSSProperties } from 'react';

import { useDictionary } from '@/core/providers/dictionary-provider';

export type FeedbackGuestFormPreviewLabels = {
  overallStars: string;
  comment: string;
  commentPlaceholder: string;
  required: string;
  yes: string;
  no: string;
  scaleLow: string;
  scaleHigh: string;
  selectPlaceholder: string;
  submit: string;
  previewNote: string;
};

type FeedbackGuestFormPreviewProps = {
  title: string;
  /** Invite "Hinweis für Gäste": shown under the title, as on the guest page. */
  contextLabel?: string | null;
  workspaceName?: string | null;
  locationName?: string | null;
  accentColor?: string | null;
  logoUrl?: string | null;
  questions: FeedbackQuestion[];
  labels: FeedbackGuestFormPreviewLabels;
  showUp2promoBranding?: boolean;
  showComment?: boolean;
  className?: string;
};

/** In-app guest form preview — same question controls as feedback-collector. */
export function FeedbackGuestFormPreview({
  title,
  contextLabel,
  workspaceName,
  locationName,
  accentColor,
  logoUrl,
  questions,
  labels,
  showUp2promoBranding = true,
  showComment = true,
  className,
}: FeedbackGuestFormPreviewProps) {
  const dict = useDictionary();
  const style = {
    '--collector-accent': accentColor?.trim() || '#2A7FFF',
  } as CSSProperties;

  return (
    <div
      className={cn(
        'relative flex h-full flex-col overflow-hidden bg-background text-foreground',
        className,
      )}
      style={style}
    >
      <div
        aria-hidden
        className="pointer-events-none absolute inset-x-0 top-0 h-40"
        style={{
          background: `radial-gradient(90% 70% at 50% -8%, color-mix(in oklab, var(--collector-accent) 28%, transparent) 0%, transparent 70%)`,
        }}
      />

      {/* pt-14 clears Dynamic Island — same preview inset as PublicProfileContent isPreview */}
      <div className="relative z-10 shrink-0 px-4 pt-14 pb-3">
        <PublicWorkspaceIdentityRow
          name={workspaceName ?? 'up2promo'}
          logoUrl={logoUrl}
          subtitle={locationName}
          size="xs"
        />
      </div>

      <div className="relative z-10 mx-3 mb-3 min-h-0 flex-1 overflow-hidden rounded-2xl bg-card shadow-md ring-1 ring-black/6">
        <div className="flex h-full min-h-0 flex-col">
          <div className="min-h-0 min-w-0 flex-1 space-y-5 overflow-x-hidden overflow-y-auto px-4 py-4 [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden">
            <div className="min-w-0 space-y-1">
              <h2 className="text-base font-semibold tracking-tight break-words">
                {title}
              </h2>
              {contextLabel ? (
                <p className="text-sm leading-relaxed break-words text-muted-foreground">
                  {contextLabel}
                </p>
              ) : null}
              <p className="text-xs text-muted-foreground">
                {labels.previewNote}
              </p>
            </div>

            <fieldset className="space-y-2" disabled>
              <legend className="text-sm font-medium">
                {labels.overallStars}
              </legend>
              <div className="flex min-w-0 justify-between gap-0.5">
                {[1, 2, 3, 4, 5].map((star) => (
                  <span
                    key={star}
                    className="shrink rounded-lg p-1 text-[color:var(--collector-accent)]"
                  >
                    <Star
                      className="size-5"
                      strokeWidth={1.5}
                      fill="currentColor"
                    />
                  </span>
                ))}
              </div>
            </fieldset>

            <GuestFeedbackQuestionFields
              questions={questions}
              labels={{
                required: labels.required,
                yes: labels.yes,
                no: labels.no,
                scaleLow: labels.scaleLow,
                scaleHigh: labels.scaleHigh,
                selectPlaceholder: labels.selectPlaceholder,
              }}
              disabled
              preview
              className="space-y-5 lg:space-y-5"
            />

            {showComment ? (
              <div className="space-y-2">
                <Label className="text-sm font-medium">{labels.comment}</Label>
                <Textarea
                  disabled
                  placeholder={labels.commentPlaceholder}
                  className="min-h-20 resize-none"
                />
              </div>
            ) : null}
          </div>

          {/* pb-8 clears home-indicator zone inside the iPhone mock */}
          <div className="shrink-0 border-t border-border/60 bg-card/95 px-4 pt-3 pb-8">
            <div className="flex h-10 items-center justify-center rounded-md bg-foreground text-sm font-medium text-background">
              {labels.submit}
            </div>
            {showUp2promoBranding ? (
              <PublicProfileBranding
                compact
                dictionary={dict}
                className="mt-2"
              />
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
}
